import type { Role } from './enums';
import { scopeAxisOf, type PermissionModule } from './permission-catalog';

/**
 * Матрица «ось роли × модуль» (план §8, инвариант 4; ADR 0106, решение 6, инвариант 5) — барьер
 * выдачи, который **нельзя проверить по эффективным правам постфактум**: к моменту, когда набор
 * выдан, данные уже показаны, и запоздалый отказ не отменяет показ, а описывает утечку.
 *
 * Смысл барьера в одной фразе плана: *scope-neutral — свойство модуля, а не набора*. Набор не
 * расширяет область только там, где область вообще есть. У путевых листов и гаража объектной
 * фильтрации нет вовсе: сегодня их закрывает не предикат, а то, что площадочным ролям
 * `waybills.read` и `garage.read` просто не выдают (`ROLE_PERMISSIONS`; ADR 0037 — в листе
 * персональные данные водителя). Набор с таким правом, выданный роли с объектной осью, откроет
 * данные всей компании, и `GRANT_MODULE_WIDE_SCOPE` тут ни при чём — расширять нечего, там
 * изначально всё.
 *
 * Отдельным файлом от `grants.ts`, потому что отвечает на другой вопрос. `grants.ts` — «что нельзя
 * выдать, что без чего не имеет смысла и что нельзя свести в одном человеке», то есть свойства
 * прав и наборов. Здесь — «где у модуля вообще есть область», свойство **модуля** и слоя
 * `apps/api/src/lib/access.ts`. Обоснование клетки читается вместе с предикатом, который её
 * оправдывает, и разъезжаться им нельзя: строка матрицы врёт молча.
 *
 * **Значения выведены из фактического состояния кода, а не из замысла.** Каждая клетка `scoped`
 * называет функцию слоя области, которая эту ось в этом модуле действительно фильтрует; каждая
 * `forbidden` означает, что такой функции нет, и до её появления выдача запрещена. Сомнение
 * решается в пользу `forbidden`: неверный запрет виден администратору сразу (набор не сохраняется),
 * неверное разрешение — никогда.
 *
 * **Матрица отвечает только про область.** «Право слишком сильное для этой роли» — вопрос каталога
 * полномочий и конструктора, а не этой таблицы: `directories.import` в клетке `global` разрешён
 * ровно в том смысле, что сужать справочник нечем, а не в том, что его стоит выдавать площадке.
 *
 * **Матрица ограничивает выдачу, а не матрицу ролей.** Роль даёт свои права мимо неё
 * (`ROLE_PERMISSIONS`), и `forbidden` в клетке никогда не означает «у этой роли такого права нет
 * сегодня» — только «набором ей это право не выдают». Обратное соотношение при этом обязано
 * держаться, и оно закреплено тестом: клетка `forbidden` там, где роль с этой осью право уже имеет,
 * была бы запретом на уже открытые данные — то есть ошибкой в матрице, а не защитой.
 */

/**
 * Оси области — четыре живых плюс «своей оси нет» (ADR 0025, 0038, 0040, 0062, 0102). Пятое
 * значение заведено вместо `null` намеренно: `Record` по объединению не даст забыть строку, а
 * `null` в ключе матрицы означал бы клетку, которую компилятор не проверяет, — ровно ту, где
 * стоит `global` и где промах стоит дороже всего.
 */
export const ROLE_SCOPE_AXES = ['object', 'department', 'counterparty', 'person', 'none'] as const;
export type RoleScopeAxis = (typeof ROLE_SCOPE_AXES)[number];

/** Ось словами — для текста отказа: администратор читает «объекты строительства», а не `object`. */
export const roleScopeAxisLabels: Record<RoleScopeAxis, string> = {
  object: 'объекты строительства',
  department: 'отделы',
  counterparty: 'контрагент',
  person: 'работник справочника',
  none: 'своей оси у роли нет',
};

/**
 * Ось роли для проверки выдачи. Обёртка над витринным `scopeAxisOf`, а **не** второй разбор ролей
 * по спискам `enums.ts`: классификация ролей по осям обязана быть одна на портал и на выдачу.
 * Разъехавшись, вторая копия ошибётся в самую дорогую сторону — роль с осью попадёт в строку
 * `none`, где всё `global`, и барьер выключится ровно для той роли, которую он защищает. Поэтому
 * соответствие каждой роли своей оси закреплено тестом по спискам `*_SCOPED_ROLES`, а не доверием
 * к витрине.
 *
 * `null` роли даёт `none` только формально: учётка без роли прав не получает вовсе (`can`
 * спрашивает роль первой), и до матрицы такая выдача не доходит.
 */
export function roleScopeAxis(role: Role | null | undefined): RoleScopeAxis {
  return scopeAxisOf(role) ?? 'none';
}

/**
 * Что матрица говорит про клетку:
 *
 * - `scoped` — у модуля есть предикат области для этой оси, и набор его не отменяет: право можно
 *   выдавать, держатель увидит своё;
 * - `global` — модуль по смыслу общий, сужать нечего: строки не делятся ни по объектам, ни по
 *   отделам, ни по контрагентам, и такой колонки в схеме нет вовсе;
 * - `forbidden` — область у модуля не выражена ничем, а у роли она есть: право этого модуля,
 *   выданное такой роли, отдаст ей данные всей компании. Запрет действует до появления предиката —
 *   выбор ровно один из двух, и второй дороже.
 */
export type GrantScopeRule = 'scoped' | 'global' | 'forbidden';

/**
 * Клетки матрицы, разложенные **по модулям**: обоснование — свойство модуля, а не оси, и живёт оно
 * одним блоком рядом с предикатом, который называет. Разложи таблицу по осям — и объяснение
 * «почему у путевых листов нельзя» пришлось бы повторить в четырёх строках, а расходиться четыре
 * копии начали бы с первой правки.
 *
 * `Record` по обеим осям — обязательное условие барьера: новый модуль витрины и новая ось области
 * не соберутся, пока про них не приняли решение. Забытая клетка иначе означала бы `undefined` —
 * то есть «не запрещено», худшее из трёх значений.
 *
 * **Строка `person` запрещена целиком, и это не осторожность.** Ось `person` — это ровно роль
 * `driver` (`PERSON_SCOPED_ROLES`), а ей наборы не выдаются ни одним способом (план §8,
 * инвариант 6). Вторая причина независима от первой: область этой оси не выражена предикатом
 * вовсе — кабинет не принимает `personId` ни в пути, ни в теле, ни в фильтре и берёт человека из
 * принципала (`routes/driver.ts`). За пределами кабинета фильтрации по работнику нет ни в одном
 * модуле, поэтому любое чужое право ушло бы водителю без области. Клетка `scoped` в этой строке
 * утверждала бы, что есть модуль, куда водителю можно что-то выдать; такого модуля нет.
 *
 * **Строка `none` — `global` везде, кроме кабинета.** Роль без своей оси не ограничена ничем по
 * построению (витринный `UNSCOPED_TEXT`): сужать ей нечего, и `forbidden` был бы запретом без
 * причины — он отрезал бы от наборов ровно диспетчера, менеджера и службу главного механика, то
 * есть тех, ради кого каталог полномочий и собирается.
 */
export const GRANT_SCOPE_MATRIX: Record<PermissionModule, Record<RoleScopeAxis, GrantScopeRule>> = {
  /**
   * Справочники — один список на компанию: колонки, по которой их можно сузить, в схеме нет вовсе
   * (`objects.ts`, `counterparties.ts`, `vehicles.ts` и соседи закрыты только правом), и
   * `directories.read` сегодня есть у **каждой** роли с осью — данные этим ролям и так открыты,
   * набор ничего не открывает заново. Это и есть `global`: не «фильтровать забыли», а «делить
   * нечего».
   */
  directories: {
    object: 'global',
    department: 'global',
    counterparty: 'global',
    // Осознанный запрет: у водителя `directories.read` нет намеренно — кабинет самодостаточен и
    // идентификаторов справочных записей в его ответах быть не должно (ADR 0102). Выдать
    // справочники набором значило бы отменить это решение молча.
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Карточки водителей — персональные данные (ADR 0037), и предиката области у модуля нет ни
   * одного: `routes/drivers.ts` закрыт правами `drivers.read`/`drivers.write` и больше ничем.
   * Роль с осью получила бы вместе с правом справочник людей всей компании.
   */
  drivers: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * **Бывший пример инварианта, ставший его подтверждением** (ADR 0192). До того выката клетки обеих
   * площадочных осей стояли `forbidden` ровно по правилу этого файла: предиката у модуля не было ни
   * одного, и закрытость журнала держалась только тем, что площадочным ролям право не выдают.
   * Заказчик попросил открыть листы площадке — и правило сработало так, как задумано: сперва
   * появился предикат (`waybillVisibilityWhere`, `assertWaybillScope` в `lib/access.ts`), и лишь
   * вслед за ним клетка сменила значение. Порядок обратный — сменить клетку и «дописать область
   * потом» — означал бы релиз, в котором набор уже выдают, а листы всей компании ещё открыты.
   *
   * Ось листа **производная**, и в этом всё своеобразие модуля: своей колонки заказчика у листа нет
   * вовсе. Область считается по талонам — заявкам, которые машина выполняет по этому листу
   * (`waybill_requests` → `vehicle_requests`), — плюс ветвь ЭСМ-2 через `waybills.source_request_id`,
   * у которого рейса нет, а заявка-основание есть. Отсюда два принятых следствия, названные в
   * ADR 0192: лист, у которого заявок нет вовсе (пустой бланк ADR 0071, рейс-перегон), площадке не
   * виден ни при каких условиях — считать по чему; а смешанный лист виден целиком тому, чей в нём
   * хотя бы один талон (решение Р2), потому что бумага у рейса одна и режется она только по живому.
   *
   * `counterparty` остаётся `forbidden`, и это не забытая строка: у арендодателя ветви в предикате
   * нет (лист выписан на машину, а не на её владельца), и набор с правом листов, выданный роли
   * исполнителя, отдал бы ей журнал всей компании вместе с персональными данными водителей.
   * `person` — тем же правилом, что у всей строки: наборы водителю не выдаются вовсе.
   */
  waybills: {
    object: 'scoped',
    department: 'scoped',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Вывоз мусора — модуль с самой полной областью: объектная роль ограничена своими объектами,
   * роль отдела — площадкой своего отдела (ADR 0062), исполнитель — своим контрагентом.
   * Предикаты: `placeObjectVisibilityWhere`, `assertPlaceObjectScope`, `operatorVisibilityWhere`.
   */
  waste: {
    object: 'scoped',
    department: 'scoped',
    // Оператор вывоза видит назначенные ему заявки; контрагент другого типа — ни одной
    // (`counterpartyVisibilityWhere` подставляет несуществующий id, а не «без ограничений»).
    counterparty: 'scoped',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Заказ ТС: `vehicleRequestVisibilityWhere` и `assertRequestScope` разбирают обе площадочные оси
   * (объект заявки против объектов учётки, отдел против отделов), арендодателя — `lessorVisibilityWhere`
   * по арендодателю назначенной машины (ADR 0038).
   */
  vehicle: {
    object: 'scoped',
    department: 'scoped',
    counterparty: 'scoped',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Механизация (план `docs/mechanization-module-plan.md`, Р10) — область считается **одной**
   * колонкой `object_id`, местом эксплуатации: объектная ось сравнивает со своими объектами,
   * отдельская — с площадками своих отделов (ADR 0062, ADR 0144). Предикаты те же, что у вывоза, —
   * `placeObjectVisibilityWhere` и `assertPlaceObjectScope`, — и берутся они как есть, а не
   * копируются: ось у обоих модулей одна и та же.
   *
   * Контрагент — `forbidden`, и это не осторожность на всякий случай. Арендодатель механизации в
   * портал не входит вовсе (Р6): учёток за таким типом не заводят, ветки по арендодателю у
   * предиката области нет, и набор с правом механизации, выданный роли исполнителя, отдал бы ей
   * аренды всех площадок компании — вместе со ставками и суммами.
   */
  mech: {
    object: 'scoped',
    department: 'scoped',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Недельная заявка: `weeklyRequestReadScope` и `assertWeeklyRequestScope` считают область парой
   * «право + ось» (план §11) и последней веткой отвечают «не видит ничего» — то есть ось, которой
   * неделя не выражается, получает пустоту, а не всё. Объектная ось — площадки учётки, отдельская —
   * площадка её отдела (ADR 0062), арендодатель — заявки со своей техникой в составе.
   *
   * Отсюда и `scoped` в трёх клетках: набор с недельным правом, выданный роли с осью, открывает ей
   * ровно её площадки. Комендант недель не видит потому, что недельных прав у него нет вовсе, —
   * закрыт модуль правом, а не именем роли в предикате.
   */
  weekly: {
    object: 'scoped',
    department: 'scoped',
    counterparty: 'scoped',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Заявки на обслуживание оргтехники: `serviceRequestScopeWhere` и `assertServiceRequestScope`
   * (объект техники и оба отдела — подавший заявку и владелец единицы), исполнитель —
   * `serviceExecutorVisibilityWhere`. Сквозная область визы ИТ приходит системным набором
   * (`GRANT_MODULE_WIDE_SCOPE`) и матрицу не отменяет: она про то, кому право можно выдать, а не
   * про то, что держатель увидит.
   */
  service: {
    object: 'scoped',
    department: 'scoped',
    counterparty: 'scoped',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Справочник оргтехники: `officeEquipmentScopeWhere` и `assertOfficeEquipmentScope` фильтруют
   * объект единицы и отдел-владельца — обе площадочные оси на месте.
   */
  officeEquipment: {
    object: 'scoped',
    department: 'scoped',
    /**
     * Ветки контрагента у предиката нет — и это не пропуск, а действующее решение ADR 0085:
     * «Справочника оргтехники у сервиса нет намеренно: „его“ техника в справочнике ничем не
     * отмечена, и право означало бы доступ ко всему парку компании — реквизиты нужной единицы
     * приходят снимком в самой заявке». Набор с `officeEquipment.read`, выданный сервисной
     * компании, ровно это и сделает: пометить «свою» технику в схеме нечем.
     */
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Гараж — второй пример плана рядом с путевыми листами: `routes/garage.ts` закрыт одним
   * `app.requirePermission('garage.read')` и не спрашивает ни объект, ни отдел, ни контрагента.
   * День парка собирается по всей технике компании, и сузить его сегодня нечем.
   */
  garage: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Кабинет водителя запрещён **всем** осям, включая `none`, и это единственный столбец, где
   * `global` не стоит нигде. Оба права кабинета невыдаваемы (`NON_GRANTABLE_PERMISSIONS`), так что
   * до матрицы такая выдача не доходит вовсе, — но второй замок здесь не лишний: убери кто-нибудь
   * права кабинета из запрета, и матрица останется единственным, что мешает выдать чужое задание
   * пакетом. Область кабинета — человек из принципала; роль без person-оси человека не имеет, и
   * кабинет ответит ей либо отказом, либо заданием того, к чьей карточке учётка привязана.
   */
  driverCabinet: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'forbidden',
  },

  /**
   * Показания техники: `routes/vehicle-readings.ts` закрыт двумя правами и ничем больше. Показания
   * висят на машине, а машина ничьей площадке не принадлежит — колонки для области нет, и предикат
   * писать пока не по чему.
   */
  vehicleReadings: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Техобслуживание — то же, что показания: `routes/vehicle-maintenance.ts` спрашивает право и
   * только его. Модуль заведён для службы главного механика, у которой своей оси нет по должности
   * (ОГМ отвечает за технику компании, а не за площадку).
   */
  vehicleMaintenance: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Архив и откаты — модуль поперёк разделов, и клетка выведена по **самому слабому** его праву.
   * `archive.read` области не отменяет: `archiveWhere` встаёт рядом с предикатами модуля-хозяина
   * одним `and`, и удалённые строки приходят из своей же области. `archive.restore` тоже: все
   * четыре ручки возврата, у сущности которых область есть, спрашивают её теми же функциями, что
   * карточка и правка (`assertPlaceObjectScope` + `assertOperatorScope`, `assertRequestScope` +
   * `assertLessorScope`, `assertServiceRequestScope` + исполнитель, `assertOfficeEquipmentScope`),
   * и спрашивают до разбора «удалена ли строка»; у контрагентов, техники и учёток области нет ни у
   * одного действия модуля, и проверять там нечего.
   *
   * Слабое место теперь одно — `records.purge`. Общая ручка удаления насовсем
   * (`services/directory-purge.ts`) поднимает строку по id и области не знает вовсе: у неё нет ни
   * параметра для проверки, ни места, куда его передать, — конфигурация справочника описывает
   * каскад и журнал, а не принадлежность. Пока это так, права модуля роли с осью выдавать нельзя:
   * `archive.restore` сам по себе уже безопасен, но клетка отвечает за модуль целиком.
   */
  records: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Файлы. Чтение файла область уважает — `canAccessFile` ищет связь теми же предикатами, что
   * списки модулей-хозяев, — но своего предиката области у файлового слоя нет, и оба права модуля
   * этим пользуются.
   *
   * `files.manageAny` означает ровно обход владения: удаление чужого файла проверяет автора
   * загрузки либо это право, и больше ничего. Роль с осью получила бы вместе с ним чужие вложения
   * на удаление.
   *
   * `files.quarantineAudit` (план `docs/office-equipment-on-site-and-invoice-estimate-plan.md`,
   * Р6, п. 4) — ещё шире: он открывает содержимое запертого файла РАНЬШЕ любой ветки видимости в
   * `canAccessFile`, то есть мимо областей всех модулей-хозяев сразу. Сузить его осью нечем и не
   * нужно: карантин ставят по инциденту, а самый частый инцидент — документ, попавший на ЧУЖУЮ
   * площадку, и область оставила бы без разбора ровно его.
   *
   * Клетка выведена по слабейшему из двух: запрет по всем осям держится уже на `files.manageAny`,
   * а право разбора карантина только подтверждает его — модуль отвечает целиком, и выдать роли с
   * осью нельзя ни одно из двух. Появись у файлов право, область уважающее, клетка от этого не
   * откроется: ослабить её можно лишь тогда, когда предикат области появится у всего модуля.
   */
  files: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Руководства — данные всей компании, и области у них нет ни по одной оси: строка знает название,
   * ссылку и порядок, а колонки, по которой список можно сузить, в схеме нет вовсе (`app_manuals`,
   * `docs/manuals-plan.md` §3.1). Это `global`, а не «фильтровать забыли»: делить нечем, да и
   * делить нечего — сам список читают все вошедшие, и право закрывает только его ведение.
   *
   * Практический смысл клетки: набор с `manuals.manage` можно выдать диспетчеру или менеджеру, но
   * не площадке и не отделу — руководства пишет один человек на компанию, а не по одному на
   * объект. Строка `person` запрещена по общему правилу: наборы водителю не выдаются вовсе.
   */
  manuals: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Аналитика — свод по всей организации по построению (план
   * `docs/analytics-summary-export-plan.md`, Р3): книга, в которой часть площадок невидима,
   * несравнима сама с собой между двумя людьми, и предиката области у ручки нет ни одного —
   * обладателю с объектной осью она отвечает отказом, а не усечённым сводом. Поэтому у всех осей
   * с областью — `forbidden`: клетка `scoped` обещала бы сужение, которого не происходит, а
   * `global` выдала бы руководителю площадки цифры всей компании.
   */
  analytics: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },

  /**
   * Администрирование: журнал действий и рассылки — данные всей компании по построению.
   * `routes/audit.ts` и `routes/admin-mailings.ts` закрыты правами и не сужаются ничем, да и
   * сужать журнал по объекту бессмысленно — он про действия людей, а не про строки площадки.
   * Будущие наборы «Аудитор» и «Рассылки» (§10 плана) поэтому уходят ролям без оси, и это не
   * ограничение реформы, а её условие.
   */
  admin: {
    object: 'forbidden',
    department: 'forbidden',
    counterparty: 'forbidden',
    person: 'forbidden',
    none: 'global',
  },
};

/**
 * Правило для конкретной роли и модуля — то, что спрашивают проверка выдачи, будущий конструктор и
 * тесты. Роль, а не ось, в параметрах намеренно: ось выводится здесь, одним способом, и вызывающий
 * не получает соблазна вывести её у себя.
 */
export function grantScopeRule(role: Role, module: PermissionModule): GrantScopeRule {
  return GRANT_SCOPE_MATRIX[module][roleScopeAxis(role)];
}
